import { Calendar } from '@/components/ui/calendar';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import type { AnalyticsLine } from '@octave/contracts';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { parsePhoneNumberWithError } from 'libphonenumber-js';
import {
  CalendarIcon,
  Check,
  ChevronsUpDown,
  Loader2,
  Phone,
  Wifi,
  MessageSquare,
} from 'lucide-react';
import { useState } from 'react';
import { DateRange } from 'react-day-picker';

function formatPhoneNumber(number: string, isDataOnly?: boolean): string {
  if (!number) return '';
  try {
    if (isDataOnly) {
      const phone = parsePhoneNumberWithError(number, 'FR');
      const nationalNumber = phone.nationalNumber;
      if (nationalNumber.startsWith('7')) {
        const remainingDigits = nationalNumber.substring(1);
        const formattedGroups = remainingDigits.match(/.{1,2}/g) || [];
        return `07 ${formattedGroups.join(' ')}`;
      }
      const formattedGroups = nationalNumber.match(/.{1,2}/g) || [];
      return `07 ${formattedGroups.join(' ')}`;
    } else {
      const phone = parsePhoneNumberWithError(number, 'FR');
      return phone.formatNational();
    }
  } catch {
    return number;
  }
}

// ─── Date shortcuts ──────────────────────────────────

const DATE_SHORTCUTS = [
  {
    label: 'Ce mois',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth(), 1),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    },
  },
  {
    label: 'Mois dernier',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
        to: new Date(today.getFullYear(), today.getMonth(), 0),
      };
    },
  },
  {
    label: '3 mois',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth() - 2, 1),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    },
  },
  {
    label: '6 mois',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), today.getMonth() - 5, 1),
        to: new Date(today.getFullYear(), today.getMonth() + 1, 0),
      };
    },
  },
  {
    label: 'Cette année',
    getRange: (): DateRange => {
      const today = new Date();
      return {
        from: new Date(today.getFullYear(), 0, 1),
        to: new Date(today.getFullYear(), 11, 31),
      };
    },
  },
];

function getMatchingShortcut(dateRange: DateRange): string | null {
  if (!dateRange.from || !dateRange.to) return null;
  const fromTime = dateRange.from.getTime();
  const toTime = dateRange.to.getTime();
  for (const shortcut of DATE_SHORTCUTS) {
    const range = shortcut.getRange();
    if (
      range.from &&
      range.to &&
      fromTime === range.from.getTime() &&
      toTime === range.to.getTime()
    ) {
      return shortcut.label;
    }
  }
  return null;
}

// ─── Product type config ─────────────────────────────

const PRODUCT_TYPES = [
  { value: 'voice' as const, label: 'Voix', icon: Phone },
  { value: 'data' as const, label: 'Data', icon: Wifi },
  { value: 'sms-mms' as const, label: 'SMS / MMS', icon: MessageSquare },
];

// ─── Types ───────────────────────────────────────────

interface AnalyticsFiltersProps {
  lines: AnalyticsLine[];
  selectedLineId: string | undefined;
  onLineChange: (lineId: string) => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  productType: 'data' | 'voice' | 'sms-mms';
  onProductTypeChange: (type: 'data' | 'voice' | 'sms-mms') => void;
  isLoading?: boolean;
}

// ─── Component ───────────────────────────────────────

export function AnalyticsFilters({
  lines,
  selectedLineId,
  onLineChange,
  dateRange,
  onDateRangeChange,
  productType,
  onProductTypeChange,
  isLoading = false,
}: AnalyticsFiltersProps) {
  const [lineSelectOpen, setLineSelectOpen] = useState(false);
  const [dateRangePickerOpen, setDateRangePickerOpen] = useState(false);

  const matchingShortcut = getMatchingShortcut(dateRange);

  // Selected line display
  const selectedLine = lines.find((l) => l.id === selectedLineId);
  const selectedLineDisplay = selectedLine
    ? (() => {
        const num = formatPhoneNumber(selectedLine.number);
        const name =
          selectedLine.identity?.firstName && selectedLine.identity?.lastName
            ? `${selectedLine.identity.firstName} ${selectedLine.identity.lastName}`
            : '';
        return name ? `${num} - ${name}` : num;
      })()
    : null;
  const selectedPlanName = selectedLine?.phonePlan?.name ?? null;

  return (
    <div className="rounded-2xl bg-white p-5 shadow-sm space-y-4">
      {/* Line selector */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
          Ligne
        </label>
        <Popover open={lineSelectOpen} onOpenChange={setLineSelectOpen}>
          <PopoverTrigger asChild>
            <button className="w-full flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/50 px-4 py-2.5 text-left transition-colors hover:bg-slate-50 hover:border-slate-300">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-50">
                <Phone className="h-3.5 w-3.5 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                {selectedLineDisplay ? (
                  <>
                    <p className="text-sm font-medium text-slate-900 truncate">
                      {selectedLineDisplay}
                    </p>
                    {selectedPlanName && (
                      <p className="text-xs text-slate-500 truncate">
                        {selectedPlanName}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="text-sm text-slate-400">
                    Sélectionner une ligne...
                  </p>
                )}
              </div>
              <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-300" />
              {isLoading && (
                <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[320px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Rechercher une ligne..." />
              <CommandList>
                <CommandEmpty>Aucune ligne trouvée.</CommandEmpty>
                <CommandGroup>
                  {lines.map((line) => {
                    const formattedNumber = formatPhoneNumber(line.number);
                    const name =
                      line.identity?.firstName && line.identity?.lastName
                        ? `${line.identity.firstName} ${line.identity.lastName}`
                        : '';
                    const planName = line.phonePlan?.name ?? '';
                    return (
                      <CommandItem
                        key={line.id}
                        value={`${line.number} ${line.id} ${name || ''} ${planName}`}
                        onSelect={(value) => {
                          const parts = value.split(' ');
                          const lineId = parts[1];
                          if (lineId) {
                            onLineChange(lineId);
                            setLineSelectOpen(false);
                          }
                        }}
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedLineId === line.id
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm truncate">
                            {name ? `${formattedNumber} - ${name}` : formattedNumber}
                          </span>
                          {planName && (
                            <span className="text-xs text-slate-400 truncate">
                              {planName}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {/* Period chips + custom calendar */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
          Période
        </label>
        <div className="flex flex-wrap items-center gap-2">
          {DATE_SHORTCUTS.map((shortcut) => {
            const isSelected = matchingShortcut === shortcut.label;
            return (
              <button
                key={shortcut.label}
                onClick={() => onDateRangeChange(shortcut.getRange())}
                className={cn(
                  'rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors',
                  isSelected
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-50 text-slate-600 border-transparent hover:bg-slate-100'
                )}
              >
                {shortcut.label}
              </button>
            );
          })}

          <Popover
            open={dateRangePickerOpen}
            onOpenChange={setDateRangePickerOpen}
          >
            <PopoverTrigger asChild>
              <button
                className={cn(
                  'rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors flex items-center gap-1.5',
                  dateRange.from && !matchingShortcut
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-50 text-slate-600 border-transparent hover:bg-slate-100'
                )}
              >
                <CalendarIcon className="h-3 w-3" />
                {dateRange.from && !matchingShortcut ? (
                  <>
                    {format(dateRange.from, 'dd MMM', { locale: fr })}
                    {dateRange.to && (
                      <> – {format(dateRange.to, 'dd MMM', { locale: fr })}</>
                    )}
                  </>
                ) : (
                  'Personnalisé'
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 max-w-none" align="start">
              <Calendar
                mode="range"
                defaultMonth={dateRange.from || new Date()}
                selected={dateRange}
                onSelect={(range) => {
                  if (range?.from && range?.to) {
                    onDateRangeChange(range as DateRange);
                    setDateRangePickerOpen(false);
                  } else if (range?.from) {
                    onDateRangeChange({ from: range.from, to: dateRange.to });
                  }
                }}
                numberOfMonths={2}
                locale={fr}
                disabled={(date) => date > new Date()}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Product type chips */}
      <div className="space-y-2">
        <label className="text-xs font-medium text-slate-400 uppercase tracking-wider">
          Type de produit
        </label>
        <div className="flex items-center gap-2">
          {PRODUCT_TYPES.map((pt) => {
            const isSelected = productType === pt.value;
            return (
              <button
                key={pt.value}
                onClick={() => onProductTypeChange(pt.value)}
                className={cn(
                  'rounded-full px-3.5 py-1.5 text-xs font-medium border transition-colors flex items-center gap-1.5',
                  isSelected
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-slate-50 text-slate-600 border-transparent hover:bg-slate-100'
                )}
              >
                <pt.icon className="h-3 w-3" />
                {pt.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
